/**
 * useLogin Composable
 * 
 * A composable that handles user authentication functionality including login and logout.
 * It manages the login form state, handles form submission, and provides error handling.
 * 
 * Features:
 * - Form state management (email, password)
 * - Login form submission with validation
 * - Error handling and loading states
 * - Logout functionality
 * - Automatic redirection after successful login
 * 
 * @returns {Object} An object containing form state, loading state, error state, and auth methods
 */

import { ref, Ref } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import { useAuthStore } from '@/stores/auth'
import axios from 'axios'
import api from '@/services/api'

type UseLoginArgs = {
	email: Ref<string>
	password: Ref<string>
	rememberMe: Ref<boolean>
}

/**
 * Get safe redirect URL based on user role
 * @param roleLower - Lowercase user role ('admin' | 'employee' | etc)
 * @param redirectRaw - Optional raw redirect path to validate
 * @returns Safe redirect URL
 */
export function getSafeRedirect(roleLower: string, redirectRaw = ''): string {
	const defaultHome =
		roleLower === 'employee' ? '/employee-dashboard' :
		roleLower === 'admin' ? '/admin-dashboard' :
		'/auth/login'

	// Validate redirect path per role
	if (roleLower === 'employee') {
		const isValidEmployeeRoute = redirectRaw.startsWith('/employee-') || redirectRaw.startsWith('/take-exam') || redirectRaw.startsWith('/profile')
		return isValidEmployeeRoute ? redirectRaw : defaultHome
	} else if (roleLower === 'admin') {
		const isValidAdminRoute = redirectRaw.startsWith('/topic') || redirectRaw.startsWith('/employees') || redirectRaw.startsWith('/exams') || redirectRaw.startsWith('/admin')
		return isValidAdminRoute ? redirectRaw : defaultHome
	}

	return defaultHome
}

export function useLogin({ email, password, rememberMe }: UseLoginArgs) {
	const router = useRouter()
	const route = useRoute()
	const authStore = useAuthStore()

	const loading = ref(false)
	const error = ref('')

	const handleLogin = async () => {

		error.value = ''

		if (!email.value || !password.value) {
			error.value = 'Missing email/password'

			console.log(error.value);
			
			return
		}
		

		loading.value = true
		try {
			// await api.get('/sanctum/csrf-cookie')
			
			const { data } = await api.post('/auth/login', {
				email: email.value,
				password: password.value,
				remember: rememberMe.value,
			})

			// Expect: { token, user, role? }
			if (data?.token) {
				// Always use localStorage (ignore rememberMe for storage type)
				localStorage.setItem('token', data.token)
				if (data.user) localStorage.setItem('user', JSON.stringify(data.user))
				
				// Clean up any old sessionStorage data
				sessionStorage.removeItem('token')
				sessionStorage.removeItem('user')

				api.defaults.headers.common.Authorization = `Bearer ${data.token}`
				axios.defaults.headers.common.Authorization = `Bearer ${data.token}`

				authStore.token = data.token
				if (data.user) authStore.user = data.user
			}

			const roleLower = (data.user?.role || '').toLowerCase()

			// Use shared redirect logic
			const redirectRaw = (route.query.redirect as string) || ''
			const safeRedirect = getSafeRedirect(roleLower, redirectRaw)

			router.replace(safeRedirect)

		} catch (err: any) {
			error.value = err?.response?.data?.message || 'Login failed'
			console.error('Login error:', err?.response?.data || err)
		} finally {
			loading.value = false
		}
	}

	const handleLogout = async () => {
		try {
			const token = authStore.token || localStorage.getItem('token')
		if (token) {
			await api.post('/logout', {}, { headers: { Authorization: `Bearer ${token}` } })
		}
		} catch (err) {
			console.error('Logout error:', err)
		} finally {
			localStorage.removeItem('token')
			localStorage.removeItem('user')
			delete api.defaults.headers.common.Authorization
			delete axios.defaults.headers.common.Authorization

			authStore.user = null
			authStore.token = null

			router.push('/login')
		}
	}

	return {
		loading,
		error,
		handleLogin,
		handleLogout
	}
}
